import { delay, http, HttpResponse, type PathParams } from 'msw';
import chargingScheduleVehicles from './data/charging-schedule-vehicles';
import fleetRoutes from './data/fleet-routes';
import getPowerNeedVehicles from './data/get-power-need-vehicles';
import getVehicleRoute from './data/get-vehicle-route';
import getVehicleOverview from './data/get-vehicle-overview';
import vehicleSpecificationStatistics from './data/vehicle-specification-statistics';
import waypoints from './data/waypoints';
import ptoDevices from './data/pto-devices';
import getPtoSummary from './data/get-pto-summary';
import getBatterySummary, { getBatteryLife } from './data/get-battery-summary';
import { SOLUTION_ENDPOINT } from './config';
import { type GetVehicleRouteEnvelope } from '@/app/services/solution/types/get-vehicle-route';
import { type GetVehicleResponseVM } from '@/app/services/solution/types/get-vehicle';
import { type SimpleFleetRoutesEnvelope } from '@/app/services/solution/types/get-simple-fleet';
import { type WaypointsEnvelope } from '@/app/services/solution/types/get-waypoints';
import { type PowerNeedEnvelope } from '@/app/services/solution/types/get-power-need';
import {
  type ChargingScheduleEnvelope,
  type ChargingScheduleVehicle,
} from '@/app/services/solution/types/get-charging-schedule';
import { type GetVehicleOverviewEnvelope } from '@/app/services/solution/types/get-vehicle-overview';
import { type ApiEnvelopeNoContent } from '@/app/services/solution/types/api';
import { type GetVehicleSpecificationStatisticsEnvelope } from '@/app/services/solution/types/get-vehicle-specification-statistics';
import {
  type GetPtoDevicesEnvelope,
  type PtoPerCycleDeviceKey,
  type PtoPerDayDeviceKey,
} from '@/app/services/solution/types/get-pto-devices';
import { type GetPtoSummaryEnvelope } from '@/app/services/solution/types/get-pto-summary';
import { type GetBatterySummaryEnvelope } from '@/app/services/solution/types/get-battery-summary';
import { type UpdateVehicleOperationDaysPerYearArguments } from '@/app/services/solution/types/update-vehicle-operation-days-per-year';

export const handlers = [
  http.get<PathParams, never, GetBatterySummaryEnvelope>(
    `${SOLUTION_ENDPOINT}/api/v3/solutions/*/vehicles/*/battery-summary`,
    async ({ request, params }) => {
      await delay(800);

      const url = new URL(request.url);

      const batterySummaryArguments = {
        solutionId: params[0] as string,
        vehicleId: params[1] as string,
        operatingDaysPerYear: Number(url.searchParams.get('operatingDaysPerYear') ?? 0),
        yearsOfOwnership: Number(url.searchParams.get('yearsOfOwnership') ?? 0),
      };

      return HttpResponse.json({
        success: true,
        hasErrors: false,
        content: getBatterySummary({
          ...batterySummaryArguments,
          vehicleId: Number(batterySummaryArguments.vehicleId),
        }),
      });
    },
  ),
  http.get<PathParams, never, GetPtoSummaryEnvelope>(
    `${SOLUTION_ENDPOINT}/api/v3/solutions/*/vehicles/*/pto-summary`,
    async ({ request, params }) => {
      await delay(800);

      const url = new URL(request.url);

      const ptoSummaryArguments = {
        solutionId: params[0] as string,
        vehicleId: params[1] as string,
        ptoDevicePerDayKey: url.searchParams.get('ptoDevicePerDayKey') as PtoPerDayDeviceKey,
        ptoDevicePerDayValue: Number(url.searchParams.get('ptoDevicePerDayValue') ?? 0),
        ptoDevicePerCycleKey: url.searchParams.get('ptoDevicePerCycleKey') as PtoPerCycleDeviceKey,
        ptoDevicePerCycleCount: Number(url.searchParams.get('ptoDevicePerCycleCount') ?? 0),
        ptoDeviceCustomKWh: Number(url.searchParams.get('ptoDeviceCustomKWh') ?? 0),
      };

      return HttpResponse.json({
        success: true,
        hasErrors: false,
        content: getPtoSummary({
          ...ptoSummaryArguments,
          vehicleId: Number(ptoSummaryArguments.vehicleId),
        }),
      });
    },
  ),
  http.get<PathParams, never, GetVehicleOverviewEnvelope>(
    `${SOLUTION_ENDPOINT}/api/v3/solutions/*/vehicles/*/overview`,
    async ({ request }) => {
      await delay(800);

      const url = new URL(request.url);
      const day = url.searchParams.get('day') ?? '';
      const temperature = url.searchParams.get('temperature') ?? '';

      return HttpResponse.json({
        success: true,
        hasErrors: false,
        content: await getVehicleOverview({ day, temperature }),
      });
    },
  ),
  http.get<PathParams, never, GetVehicleRouteEnvelope>(
    `${SOLUTION_ENDPOINT}/api/v3/solutions/*/vehicles/*/route`,
    async () => {
      await delay(800);
      return HttpResponse.json({
        success: true,
        hasErrors: false,
        content: await getVehicleRoute(),
      });
    },
  ),
  http.get<PathParams, never, GetVehicleSpecificationStatisticsEnvelope>(
    `${SOLUTION_ENDPOINT}/api/v3/solutions/*/vehicles/*/specification-statistics`,
    async () => {
      await delay(800);
      return HttpResponse.json({
        success: true,
        hasErrors: false,
        content: vehicleSpecificationStatistics,
      });
    },
  ),
  http.get<PathParams, never, PowerNeedEnvelope>(`${SOLUTION_ENDPOINT}/api/v3/solutions/*/power-need`, async () => {
    await delay(800);
    return HttpResponse.json({
      success: true,
      hasErrors: false,
      content: { items: getPowerNeedVehicles(chargingScheduleVehicles) },
    });
  }),
  http.put<PathParams, ChargingScheduleVehicle, ApiEnvelopeNoContent>(
    `${SOLUTION_ENDPOINT}/api/v2/solutions/*/charging-schedule`,
    async ({ request }) => {
      await delay(800);
      const body = await request.json();
      const index = chargingScheduleVehicles.findIndex(
        (chargingScheduleVehicle) =>
          chargingScheduleVehicle.chassisNumber === body.chassisNumber &&
          chargingScheduleVehicle.vehicleId === body.vehicleId &&
          chargingScheduleVehicle.waypointId === body.waypointId,
      );
      if (index !== -1) {
        chargingScheduleVehicles[index] = { ...chargingScheduleVehicles[index], ...body };
      }
      return HttpResponse.json({
        success: true,
        hasErrors: false,
      });
    },
  ),
  http.get<PathParams, never, ChargingScheduleEnvelope>(
    `${SOLUTION_ENDPOINT}/api/v3/solutions/*/charging-schedule`,
    async () => {
      await delay(800);
      return HttpResponse.json({
        success: true,
        hasErrors: false,
        content: { items: chargingScheduleVehicles },
      });
    },
  ),
  http.get<PathParams, never, SimpleFleetRoutesEnvelope>(
    `${SOLUTION_ENDPOINT}/api/v3/solutions/*/fleet-routes`,
    async () => {
      await delay(800);
      return HttpResponse.json({
        success: true,
        hasErrors: false,
        content: fleetRoutes,
      });
    },
  ),
  http.get<PathParams, never, WaypointsEnvelope>(`${SOLUTION_ENDPOINT}/api/v3/solutions/*/waypoints`, async () => {
    await delay(800);
    return HttpResponse.json({
      success: true,
      hasErrors: false,
      content: { items: waypoints },
    });
  }),
  http.put<PathParams, UpdateVehicleOperationDaysPerYearArguments, ApiEnvelopeNoContent>(
    `${SOLUTION_ENDPOINT}/api/v3/solutions/*/vehicles/batch/operation-days-per-year`,
    async ({ request }) => {
      await delay(800);
      const body = await request.json();
      getBatteryLife({ operatingDaysPerYear: body.operatingDaysPerYear });

      return HttpResponse.json({
        success: true,
        hasErrors: false,
      });
    },
  ),
  http.put<PathParams, GetVehicleResponseVM, ApiEnvelopeNoContent>(
    `${SOLUTION_ENDPOINT}/api/v3/solutions/*/vehicles/batch/pto`,
    async ({ request }) => {
      await delay(800);
      await request.json();

      return HttpResponse.json({
        success: true,
        hasErrors: false,
      });
    },
  ),
  http.get<PathParams, never, GetPtoDevicesEnvelope>(`${SOLUTION_ENDPOINT}/api/v3/pto-devices`, async () => {
    await delay(800);
    return HttpResponse.json({
      success: true,
      hasErrors: false,
      content: ptoDevices,
    });
  }),
];
